import React, { useEffect, useState } from 'react';
// import { Link } from 'react-router-dom'; // Não está sendo usado
import { useAuth } from 'react-oidc-context';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import PageHeader from '../components/UI/PageHeader';
import Card from '../components/UI/Card';
// Chart components are not being used directly anymore - using ReactECharts inline
import Button from '../components/UI/Button';
import {
  getUserCountByDate,
  getChatHistoryCountByVendor,
  getChatHistoryCountByDateAndApplication,
  getChatHistoryCountByDate
} from '../services/analytics/analyticsService';
import { chartColors, colorPalette } from '../components/Charts/chartUtils';

const Dashboard: React.FC = () => {
  const auth = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Date range state (default last 7 days)
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [customRange, setCustomRange] = useState(false);
  
  // Stats state
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalCompletions, setTotalCompletions] = useState(0);
  const [totalApplications, setTotalApplications] = useState(0);
  const [averageCompletionsPerDay, setAverageCompletionsPerDay] = useState(0);
  // const [topVendor, setTopVendor] = useState<string>(''); // Não está sendo usado
  
  // Chart data state
  const [vendorChartData, setVendorChartData] = useState<any>(null);
  const [userTrendData, setUserTrendData] = useState<any>(null);
  const [completionsTrendData, setCompletionsTrendData] = useState<any>(null);
  const [applicationChartData, setApplicationChartData] = useState<any>(null);
  

  // Initialize date range
  useEffect(() => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    
    const start = new Date();
    start.setDate(start.getDate() - 7); // Last 7 days
    start.setHours(0, 0, 0, 0);
    
    setEndDate(formatDateTimeForInput(end));
    setStartDate(formatDateTimeForInput(start));
  }, []);

  useEffect(() => {
    if (auth.user && startDate && endDate) {
      fetchDashboardData();
    }
  }, [auth.user, startDate, endDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDateTimeForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Convert input dates to Date objects
      const startDateTime = new Date(startDate);
      const endDateTime = new Date(endDate);
      
      // Fetch only real analytics endpoints
      const [
        vendorStatsResponse,
        userTrendResponse,
        completionsTrendResponse,
        chatByAppResponse
      ] = await Promise.all([
        getChatHistoryCountByVendor(auth.user),
        getUserCountByDate(auth.user, startDateTime.toISOString(), endDateTime.toISOString()),
        getChatHistoryCountByDate(auth.user, startDateTime.toISOString(), endDateTime.toISOString()),
        getChatHistoryCountByDateAndApplication(auth.user, startDateTime.toISOString(), endDateTime.toISOString())
      ]);
      
      // Use the responses directly as they should already be arrays
      const vendorStats = vendorStatsResponse || [];
      const userTrend = userTrendResponse || [];
      const completionsTrend = completionsTrendResponse || [];
      const chatByApp = chatByAppResponse || [];

      // Calculate totals and insights
      const totalCompletionsCount = Array.isArray(vendorStats) 
        ? vendorStats.reduce((sum, v) => sum + (Number(v?.total) || 0), 0) 
        : 0;
      setTotalCompletions(totalCompletionsCount);
      
      const totalUsersCount = Array.isArray(userTrend) 
        ? userTrend.reduce((sum, u) => sum + (Number(u?.total) || 0), 0) 
        : 0;
      setTotalUsers(totalUsersCount);
      
      // Note: We don't have a direct endpoint for total applications count
      // This would need to be fetched from a different endpoint if needed
      setTotalApplications(0);
      
      // Calculate average completions per day
      if (completionsTrend && Array.isArray(completionsTrend) && completionsTrend.length > 0) {
        const totalDays = completionsTrend.length;
        const totalCompletionsInPeriod = completionsTrend.reduce((sum, c) => sum + (Number(c?.total) || 0), 0);
        setAverageCompletionsPerDay(Math.round(totalCompletionsInPeriod / totalDays));
      }
      
      // Find top vendor
      if (vendorStats && Array.isArray(vendorStats) && vendorStats.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const sortedVendors = [...vendorStats].sort((a, b) => b.total - a.total);
        // const topVendorName = sortedVendors[0]?.vendor_name || ''; // Não usado
        // Format vendor name for display
        /* Função formatVendorName não está sendo usada
        const formatVendorName = (name: string) => {
          switch (name) {
            case 'OMNI_CHAT':
              return 'Omni Chat';
            case 'TELEGRAM':
              return 'Telegram';
            case 'TEAMS':
              return 'Teams';
            default:
              return name.replace(/_/g, ' ').toLowerCase()
                .split(' ')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
          }
        };
        */
        // setTopVendor(formatVendorName(topVendorName)); // Não está sendo usado
      }

      // Prepare vendor chart data (Column Chart)
      if (vendorStats && Array.isArray(vendorStats) && vendorStats.length > 0) {
        console.log('Processing vendor stats:', vendorStats);
        const sortedVendors = vendorStats
          .filter(v => v.total > 0) // Only include vendors with data
          .sort((a, b) => b.total - a.total); // Sort by total descending
        
        console.log('Sorted vendors:', sortedVendors);
        
        if (sortedVendors.length > 0) {
          // Use different colors for each vendor
          const vendorColors = [
            chartColors.primary,
            chartColors.success,
            chartColors.warning,
            chartColors.purple,
            chartColors.info,
            chartColors.danger,
            chartColors.pink,
            chartColors.teal
          ];
          
          // Format vendor names for better display
          const formatVendorName = (name: string) => {
            switch (name) {
              case 'OMNI_CHAT':
                return 'Omni Chat';
              case 'TELEGRAM':
                return 'Telegram';
              case 'TEAMS':
                return 'Teams';
              default:
                return name.replace(/_/g, ' ').toLowerCase()
                  .split(' ')
                  .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ');
            }
          };
          
          // Prepare data for multi-color bar chart
          const chartData = {
            labels: sortedVendors.map(v => formatVendorName(v.vendor_name || 'Unknown')),
            data: sortedVendors.map(v => Number(v.total) || 0),
            colors: sortedVendors.map((_, index) => vendorColors[index % vendorColors.length]),
          };
          setVendorChartData(chartData);
        } else {
          setVendorChartData(null);
        }
      } else {
        setVendorChartData(null);
      }

      // Prepare user trend data (Line Chart)
      if (userTrend && Array.isArray(userTrend) && userTrend.length > 0) {
        const sortedUserTrend = userTrend
          .filter(item => item.date) // Filter out items without dates
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        
        if (sortedUserTrend.length > 0) {
          const chartData = {
            labels: sortedUserTrend.map(item => {
              const date = new Date(item.date);
              // Format based on range - if more than 30 days, show month/day, otherwise show weekday/day
              const daysDiff = Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24));
              if (daysDiff > 30) {
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              } else {
                return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
              }
            }),
            datasets: [{
              label: 'New Users',
              data: sortedUserTrend.map(item => Number(item.total) || 0),
              borderColor: chartColors.success,
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              fill: true,
            }]
          };
          setUserTrendData(chartData);
        } else {
          setUserTrendData(null);
        }
      } else {
        setUserTrendData(null);
      }

      // Prepare completions trend data (Line Chart)
      if (completionsTrend && Array.isArray(completionsTrend) && completionsTrend.length > 0) {
        const sortedCompletions = completionsTrend
          .filter(item => item.date) // Filter out items without dates
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        
        if (sortedCompletions.length > 0) {
          setCompletionsTrendData({
            labels: sortedCompletions.map(item => {
              const date = new Date(item.date);
              // Format based on range - if more than 30 days, show month/day, otherwise show weekday/day
              const daysDiff = Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24));
              if (daysDiff > 30) {
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              } else {
                return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
              }
            }),
            datasets: [{
              label: 'Chat Completions',
              data: sortedCompletions.map(item => Number(item.total) || 0),
              borderColor: chartColors.primary,
              backgroundColor: 'rgba(59, 130, 246, 0.1)',
              fill: true,
            }]
          });
        } else {
          setCompletionsTrendData(null);
        }
      } else {
        setCompletionsTrendData(null);
      }

      // Prepare application usage data (Stacked Area Chart)
      if (chatByApp && Array.isArray(chatByApp) && chatByApp.length > 0) {
        // Get all unique dates and sort them
        const uniqueDates = Array.from(new Set(chatByApp.map(item => item.date)))
          .filter(date => date)
          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        
        // Group by application, keeping track of names
        const appGroups: Record<string, { name: string; data: Record<string, number> }> = {};
        
        chatByApp.forEach(item => {
          const appId = item.application_id || 'Unknown';
          const appName = item.application_name || appId;
          
          if (!appGroups[appId]) {
            appGroups[appId] = {
              name: appName,
              data: {}
            };
          }
          
          // Handle duplicates by summing the totals for the same date
          appGroups[appId].data[item.date] = (appGroups[appId].data[item.date] || 0) + Number(item.total);
        });
        
        // Calculate totals for each app to find top apps
        const appTotals = Object.entries(appGroups).map(([appId, appData]) => ({
          appId,
          name: appData.name,
          total: Object.values(appData.data).reduce((sum, val) => sum + val, 0)
        })).sort((a, b) => b.total - a.total);
        
        // Get top 5 applications by total usage
        const topApps = appTotals.slice(0, 5);
        
        if (uniqueDates.length > 0 && topApps.length > 0) {
          const daysDiff = Math.ceil((new Date(endDate).getTime() - new Date(startDate).getTime()) / (1000 * 60 * 60 * 24));
          
          setApplicationChartData({
            labels: uniqueDates.map(dateStr => {
              const date = new Date(dateStr);
              if (daysDiff > 30) {
                return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              } else {
                return date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
              }
            }),
            datasets: topApps.map((app, index) => ({
              label: app.name,
              data: uniqueDates.map(date => appGroups[app.appId].data[date] || 0),
              color: colorPalette[index % colorPalette.length],
              gradient: true,
            }))
          });
        } else {
          setApplicationChartData(null);
        }
      } else {
        setApplicationChartData(null);
      }


    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      console.error('Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : undefined
      });
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = () => {
    if (startDate && endDate) {
      fetchDashboardData();
    }
  };

  const setQuickDateRange = (days: number) => {
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    
    const start = new Date();
    start.setDate(start.getDate() - days);
    start.setHours(0, 0, 0, 0);
    
    setEndDate(formatDateTimeForInput(end));
    setStartDate(formatDateTimeForInput(start));
    setCustomRange(false);
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  if (loading && !vendorChartData && !userTrendData && !completionsTrendData) {
    return (
      <div>
        <PageHeader
          title="Dashboard"
          breadcrumbs={[{ label: "Dashboard" }]}
        />
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Dashboard"
        breadcrumbs={[{ label: "Dashboard" }]}
      />
      
      {error && (
        <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6">
          {error}
          <Button variant="ghost" size="sm" className="ml-2" onClick={fetchDashboardData}>
            Retry
          </Button>
        </div>
      )}
      
      {/* Date Range Filter */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex gap-2">
              <Button 
                variant={!customRange ? "primary" : "ghost"} 
                size="sm"
                onClick={() => setQuickDateRange(7)}
              >
                Last 7 Days
              </Button>
              <Button 
                variant={!customRange ? "ghost" : "ghost"} 
                size="sm"
                onClick={() => setQuickDateRange(30)}
              >
                Last 30 Days
              </Button>
              <Button 
                variant={!customRange ? "ghost" : "ghost"} 
                size="sm"
                onClick={() => setQuickDateRange(90)}
              >
                Last 90 Days
              </Button>
              <Button 
                variant={customRange ? "primary" : "ghost"} 
                size="sm"
                onClick={() => setCustomRange(true)}
              >
                Custom Range
              </Button>
            </div>
            
            {customRange && (
              <div className="flex gap-2 items-center">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="datetime-local"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                </div>
                <div className="mt-6">
                  <Button variant="primary" size="sm" onClick={handleDateRangeChange}>
                    Apply
                  </Button>
                </div>
              </div>
            )}
            
            <div className="ml-auto">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={fetchDashboardData}
                disabled={loading}
              >
                <i className="fas fa-sync-alt mr-2"></i>
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </Card>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Applications</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{formatNumber(totalApplications)}</p>
                <p className="text-xs text-gray-500 mt-1">Active in system</p>
              </div>
              <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                <i className="fas fa-layer-group text-blue-600"></i>
              </div>
            </div>
          </div>
        </Card>
        
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">New Users</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{formatNumber(totalUsers)}</p>
                <p className="text-xs text-gray-500 mt-1">In selected period</p>
              </div>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <i className="fas fa-users text-green-600"></i>
              </div>
            </div>
          </div>
        </Card>
        
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Chat Completions</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{formatNumber(totalCompletions)}</p>
                <p className="text-xs text-gray-500 mt-1">Total in period</p>
              </div>
              <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                <i className="fas fa-comments text-purple-600"></i>
              </div>
            </div>
          </div>
        </Card>
        
        <Card>
          <div className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Daily Average</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{formatNumber(averageCompletionsPerDay)}</p>
                <p className="text-xs text-gray-500 mt-1">Completions per day</p>
              </div>
              <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
                <i className="fas fa-chart-line text-amber-600"></i>
              </div>
            </div>
          </div>
        </Card>
      </div>
      
      
      {/* Chat Completions Over Time - Full Width */}
      <Card className="mb-6 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Chat Completions Over Time</h3>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">Total Period:</span>
              <span className="text-sm font-bold text-blue-600">
                {completionsTrendData?.datasets?.[0]?.data?.reduce((a: number, b: number) => a + b, 0).toLocaleString() || 0}
              </span>
            </div>
          </div>
        </div>
        <div className="p-2">
          <div style={{ height: '420px' }}>
            {completionsTrendData && completionsTrendData.labels && completionsTrendData.labels.length > 0 ? (
              <ReactECharts
                option={{
                  backgroundColor: 'transparent',
                  tooltip: { 
                    trigger: 'axis',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderColor: '#e5e7eb',
                    borderWidth: 1,
                    padding: [8, 12],
                    textStyle: {
                      fontSize: 12,
                      color: '#374151'
                    },
                    axisPointer: {
                      type: 'line',
                      lineStyle: {
                        color: '#9ca3af',
                        width: 1,
                        type: 'dashed'
                      }
                    }
                  },
                  xAxis: {
                    type: 'category',
                    data: completionsTrendData.labels,
                    boundaryGap: false,
                    axisLine: {
                      show: false
                    },
                    axisTick: {
                      show: false
                    },
                    axisLabel: {
                      color: '#6b7280',
                      fontSize: 11,
                      margin: 15,
                      rotate: 30
                    },
                    splitLine: {
                      show: false
                    }
                  },
                  yAxis: { 
                    type: 'value',
                    axisLine: {
                      show: false
                    },
                    axisTick: {
                      show: false
                    },
                    axisLabel: {
                      color: '#6b7280',
                      fontSize: 11,
                      formatter: (value: number) => {
                        if (value >= 1000) return (value / 1000) + 'k';
                        return value;
                      }
                    },
                    splitLine: {
                      lineStyle: {
                        color: '#f3f4f6',
                        type: 'dashed'
                      }
                    }
                  },
                  series: completionsTrendData.datasets.map((dataset: any) => ({
                    name: dataset.label,
                    type: 'line',
                    data: dataset.data,
                    smooth: 0.6,
                    symbol: 'circle',
                    symbolSize: 4,
                    sampling: 'lttb',
                    itemStyle: {
                      color: dataset.borderColor
                    },
                    areaStyle: {
                      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        {
                          offset: 0,
                          color: dataset.borderColor + '25'
                        },
                        {
                          offset: 1,
                          color: dataset.borderColor + '05'
                        }
                      ])
                    },
                    lineStyle: { 
                      color: dataset.borderColor,
                      width: 3,
                      shadowColor: dataset.borderColor + '40',
                      shadowBlur: 10,
                      shadowOffsetY: 4
                    }
                  })),
                  grid: { 
                    left: '2%', 
                    right: '2%', 
                    top: '5%', 
                    bottom: '10%', 
                    containLabel: true 
                  },
                  animationEasing: 'cubicOut',
                  animationDuration: 1000
                }}
                style={{ height: '100%', width: '100%' }}
                opts={{ renderer: 'svg' }}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <i className="fas fa-chart-line text-4xl mb-2 opacity-50"></i>
                  <p>No data available for selected period</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
      
      {/* New Users Over Time - Full Width */}
      <Card className="mb-6 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">New Users Over Time</h3>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">Total New Users:</span>
              <span className="text-sm font-bold text-green-600">
                {userTrendData?.datasets?.[0]?.data?.reduce((a: number, b: number) => a + b, 0).toLocaleString() || 0}
              </span>
            </div>
          </div>
        </div>
        <div className="p-2">
          <div style={{ height: '420px' }}>
            {userTrendData && userTrendData.labels && userTrendData.labels.length > 0 ? (
              <ReactECharts
                option={{
                  backgroundColor: 'transparent',
                  tooltip: { 
                    trigger: 'axis',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderColor: '#e5e7eb',
                    borderWidth: 1,
                    padding: [8, 12],
                    textStyle: {
                      fontSize: 12,
                      color: '#374151'
                    },
                    axisPointer: {
                      type: 'line',
                      lineStyle: {
                        color: '#9ca3af',
                        width: 1,
                        type: 'dashed'
                      }
                    }
                  },
                  xAxis: {
                    type: 'category',
                    data: userTrendData.labels,
                    boundaryGap: false,
                    axisLine: {
                      show: false
                    },
                    axisTick: {
                      show: false
                    },
                    axisLabel: {
                      color: '#6b7280',
                      fontSize: 11,
                      margin: 15,
                      rotate: 30
                    },
                    splitLine: {
                      show: false
                    }
                  },
                  yAxis: { 
                    type: 'value',
                    axisLine: {
                      show: false
                    },
                    axisTick: {
                      show: false
                    },
                    axisLabel: {
                      color: '#6b7280',
                      fontSize: 11
                    },
                    splitLine: {
                      lineStyle: {
                        color: '#f3f4f6',
                        type: 'dashed'
                      }
                    }
                  },
                  series: userTrendData.datasets.map((dataset: any) => ({
                    name: dataset.label,
                    type: 'line',
                    data: dataset.data,
                    smooth: 0.6,
                    symbol: 'circle',
                    symbolSize: 4,
                    sampling: 'lttb',
                    itemStyle: {
                      color: dataset.borderColor || dataset.color
                    },
                    areaStyle: {
                      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        {
                          offset: 0,
                          color: (dataset.borderColor || dataset.color) + '25'
                        },
                        {
                          offset: 1,
                          color: (dataset.borderColor || dataset.color) + '05'
                        }
                      ])
                    },
                    lineStyle: { 
                      color: dataset.borderColor || dataset.color,
                      width: 3,
                      shadowColor: (dataset.borderColor || dataset.color) + '40',
                      shadowBlur: 10,
                      shadowOffsetY: 4
                    }
                  })),
                  grid: { 
                    left: '2%', 
                    right: '2%', 
                    top: '5%', 
                    bottom: '10%', 
                    containLabel: true 
                  },
                  animationEasing: 'cubicOut',
                  animationDuration: 1000
                }}
                style={{ height: '100%', width: '100%' }}
                opts={{ renderer: 'svg' }}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <i className="fas fa-users text-4xl mb-2 opacity-50"></i>
                  <p>No data available for selected period</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
      
      {/* Chat Completions by Vendor - Bar Race Style */}
      <Card className="mb-6 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Chat Completions by Vendor</h3>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">Leader:</span>
              <span className="text-sm font-bold text-purple-600">
                {vendorChartData?.labels?.[0] || 'N/A'}
              </span>
            </div>
          </div>
        </div>
        <div className="p-3">
          <div style={{ height: '350px' }}>
            {vendorChartData && vendorChartData.labels && vendorChartData.labels.length > 0 ? (
              <ReactECharts
                option={{
                  backgroundColor: 'transparent',
                  tooltip: { 
                    trigger: 'axis',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderColor: '#e5e7eb',
                    borderWidth: 1,
                    padding: [8, 12],
                    textStyle: {
                      fontSize: 12,
                      color: '#374151'
                    },
                    axisPointer: {
                      type: 'shadow',
                      shadowStyle: {
                        color: 'rgba(0, 0, 0, 0.05)'
                      }
                    },
                    formatter: (params: any) => {
                      const value = params[0].value;
                      const percentage = ((value / vendorChartData.data.reduce((a: number, b: number) => a + b, 0)) * 100).toFixed(1);
                      return `${params[0].name}<br/>
                              <span style="font-weight: bold">${value.toLocaleString()}</span> completions<br/>
                              <span style="color: #9ca3af">${percentage}% of total</span>`;
                    }
                  },
                  grid: { 
                    left: '2%', 
                    right: '15%', 
                    bottom: '3%', 
                    top: '3%',
                    containLabel: true 
                  },
                  xAxis: {
                    type: 'value',
                    axisLine: {
                      show: false
                    },
                    axisTick: {
                      show: false
                    },
                    axisLabel: {
                      show: false
                    },
                    splitLine: {
                      show: false
                    }
                  },
                  yAxis: { 
                    type: 'category',
                    data: vendorChartData.labels,
                    inverse: true,
                    axisLine: {
                      show: false
                    },
                    axisTick: {
                      show: false
                    },
                    axisLabel: {
                      color: '#374151',
                      fontSize: 13,
                      fontWeight: 'bold',
                      margin: 10
                    }
                  },
                  series: [{
                    name: 'Total Completions',
                    type: 'bar',
                    barWidth: '60%',
                    data: vendorChartData.data.map((value: number, index: number) => ({
                      value,
                      itemStyle: { 
                        color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                          { offset: 0, color: vendorChartData.colors[index] + 'CC' },
                          { offset: 1, color: vendorChartData.colors[index] }
                        ]),
                        borderRadius: [0, 8, 8, 0],
                        shadowColor: vendorChartData.colors[index] + '40',
                        shadowBlur: 8,
                        shadowOffsetY: 2
                      }
                    })),
                    label: {
                      show: true,
                      position: 'right',
                      distance: 10,
                      formatter: (params: any) => {
                        const value = params.value;
                        if (value >= 1000000) {
                          return (value / 1000000).toFixed(1) + 'M';
                        } else if (value >= 1000) {
                          return (value / 1000).toFixed(1) + 'k';
                        }
                        return value.toLocaleString();
                      },
                      fontSize: 14,
                      fontWeight: 'bold',
                      color: '#374151'
                    },
                    emphasis: { 
                      focus: 'self',
                      itemStyle: {
                        shadowBlur: 15,
                        shadowOffsetX: 0,
                        shadowColor: 'rgba(0, 0, 0, 0.3)'
                      }
                    },
                    animationDelay: (idx: number) => idx * 200,
                    animationEasing: 'backOut',
                    animationDuration: 1500
                  }]
                }}
                style={{ height: '100%', width: '100%' }}
                opts={{ renderer: 'svg' }}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <i className="fas fa-server text-4xl mb-2 opacity-50"></i>
                  <p>No vendor data available</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
      
      {/* Application Usage Over Time - Full Width */}
      <Card className="mb-6 overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Application Usage Over Time</h3>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-gray-500">Top App:</span>
              <span className="text-sm font-bold text-orange-600">
                {applicationChartData?.datasets?.[0]?.label || 'N/A'}
              </span>
            </div>
          </div>
        </div>
        <div className="p-2">
          <div style={{ height: '420px' }}>
            {applicationChartData && applicationChartData.labels && applicationChartData.labels.length > 0 ? (
              <ReactECharts
                option={{
                  backgroundColor: 'transparent',
                  tooltip: { 
                    trigger: 'axis',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderColor: '#e5e7eb',
                    borderWidth: 1,
                    padding: [8, 12],
                    textStyle: {
                      fontSize: 12,
                      color: '#374151'
                    },
                    axisPointer: {
                      type: 'line',
                      lineStyle: {
                        color: '#9ca3af',
                        width: 1,
                        type: 'dashed'
                      }
                    }
                  },
                  legend: { 
                    data: applicationChartData.datasets.map((d: any) => d.label),
                    bottom: 0,
                    type: 'scroll',
                    textStyle: {
                      fontSize: 11,
                      color: '#6b7280'
                    },
                    pageIconColor: '#6b7280',
                    pageTextStyle: {
                      color: '#6b7280'
                    }
                  },
                  xAxis: {
                    type: 'category',
                    data: applicationChartData.labels,
                    boundaryGap: false,
                    axisLine: {
                      show: false
                    },
                    axisTick: {
                      show: false
                    },
                    axisLabel: {
                      color: '#6b7280',
                      fontSize: 11,
                      margin: 15,
                      rotate: 30
                    },
                    splitLine: {
                      show: false
                    }
                  },
                  yAxis: { 
                    type: 'value',
                    axisLine: {
                      show: false
                    },
                    axisTick: {
                      show: false
                    },
                    axisLabel: {
                      color: '#6b7280',
                      fontSize: 11,
                      formatter: (value: number) => {
                        if (value >= 1000) return (value / 1000) + 'k';
                        return value;
                      }
                    },
                    splitLine: {
                      lineStyle: {
                        color: '#f3f4f6',
                        type: 'dashed'
                      }
                    }
                  },
                  series: applicationChartData.datasets.map((dataset: any, index: number) => ({
                    name: dataset.label,
                    type: 'line',
                    data: dataset.data,
                    smooth: 0.6,
                    symbol: 'circle',
                    symbolSize: 4,
                    sampling: 'lttb',
                    stack: 'Total',
                    areaStyle: {
                      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        {
                          offset: 0,
                          color: dataset.color + '40'
                        },
                        {
                          offset: 1,
                          color: dataset.color + '10'
                        }
                      ])
                    },
                    lineStyle: { 
                      color: dataset.color,
                      width: 2
                    },
                    emphasis: {
                      focus: 'series',
                      areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                          {
                            offset: 0,
                            color: dataset.color + '60'
                          },
                          {
                            offset: 1,
                            color: dataset.color + '20'
                          }
                        ])
                      }
                    },
                    animationDelay: (idx: number) => idx * 10 + index * 100,
                    animationEasing: 'cubicOut'
                  })),
                  grid: { 
                    left: '2%', 
                    right: '2%', 
                    top: '5%', 
                    bottom: '15%', 
                    containLabel: true 
                  },
                  animationEasing: 'cubicOut',
                  animationDuration: 1000
                }}
                style={{ height: '100%', width: '100%' }}
                opts={{ renderer: 'svg' }}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <div className="text-center">
                  <i className="fas fa-layer-group text-4xl mb-2 opacity-50"></i>
                  <p>No application data available</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </Card>
      
    </div>
  );
};

export default Dashboard;